/**
 * Shared helper for identifying text/manual chat sources that should preview
 * in-app (via the TextSourceDrawer) rather than open a raw-text browser tab.
 *
 * Used by both source-row clicks (Sources.tsx) and in-text citation links
 * (MarkdownMessage.tsx), so the two click paths behave identically.
 */

import type { ChatSource } from '../../api/chatTypes';

/**
 * True for plain-text and manual ("Textinhalt") sources, which should preview
 * in-app rather than open a raw-text browser tab.
 *
 * Note on the data: manual free-text docs reach the frontend with
 * source_type="document" (the generic chunk type kept by the context builder)
 * and file_type="text"; .txt uploads similarly carry file_type "text"/"txt".
 * So matching ONLY on source_type misses them -- file_type is the reliable
 * signal. URL/web sources are excluded even if their file_type looks text-like.
 */
export function isTextLikeSource(source: ChatSource): boolean {
  // A real website is never an in-app text preview, regardless of file_type.
  const isHttpUrl = Boolean(source.url && /^https?:\/\//i.test(source.url));
  if (isHttpUrl) {
    return false;
  }
  const st = (source.source_type ?? '').toLowerCase();
  if (st === 'manual' || st === 'txt' || st === 'text') {
    return true;
  }
  // Generic/uploaded source_type ("document"/"file") + a text-like file_type is
  // a .txt upload, a manual free-text doc, a timed transcript (VTT/SRT — the
  // extracted transcript text previews in-app), or an ingested e-mail.
  const ft = (source.file_type ?? '').toLowerCase();
  return (
    ft === 'txt' ||
    ft === 'text' ||
    ft === 'text/plain' ||
    ft === 'vtt' ||
    ft === 'srt' ||
    ft === 'eml' ||
    ft === 'msg'
  );
}
